/**
 * @module constants/codeLabel
 * @description 코드값 → 화면 이름 변환의 단일 원천 (SURINP-679 CodeLabel).
 *
 * MANPOWER·OPERATOR·Y/N 같은 서버 코드값을 그리드·카드·사이드바에 그대로 보이지 않고
 * 로케일 이름으로 바꿔 보인다. 저장·조회에 쓰는 값은 코드 그대로다(표시만 바꾼다).
 * 이름은 서버 enum 의 description(cs-back RepairStaffTeamType·ServiceCompanyType·UserType)과 같게 둔다.
 *
 * 사용처:
 * - 그리드 열: `valueFormatter: codeLabelFormatter('<그룹>')` (agSelectCellEditor 목록도 같은 이름으로 보인다)
 * - 템플릿: `codeLabel('<그룹>', 값)`
 */
import i18n from '@/locales/i18n'

/**
 * 그룹 → 코드값 → 로케일 키.
 * 모르는 그룹·값은 원래 값을 그대로 돌려준다(빈 칸으로 숨기지 않는다).
 *
 * @type {Record<string, Record<string, string>>}
 */
export const CODE_LABEL_KEYS = {
  // 수리파트너 소속 타입 (RepairStaffTeamType)
  staffTeamType: {
    MANPOWER: 'component.codeLabel.staffTeamType.MANPOWER',
    MANUFACTURER: 'component.codeLabel.staffTeamType.MANUFACTURER',
    ETC: 'component.codeLabel.staffTeamType.ETC'
  },
  // 협력업체 타입 (ServiceCompanyType)
  serviceCompanyType: {
    MANUFACTURER: 'component.codeLabel.serviceCompanyType.MANUFACTURER',
    OPERATOR: 'component.codeLabel.serviceCompanyType.OPERATOR',
    OWNER: 'component.codeLabel.serviceCompanyType.OWNER',
    ETC: 'component.codeLabel.serviceCompanyType.ETC'
  },
  // 로그인 사용자 유형 (JWT userType)
  userType: {
    ADMIN: 'component.codeLabel.userType.ADMIN',
    OPERATOR: 'component.codeLabel.userType.OPERATOR',
    PARTNER: 'component.codeLabel.userType.PARTNER',
    REPAIR_STAFF: 'component.codeLabel.userType.REPAIR_STAFF',
    CUSTOMER: 'component.codeLabel.userType.CUSTOMER'
  },
  // 사용 여부
  useYn: {
    Y: 'common.use',
    N: 'common.notUse'
  },
  // 대고객 앱 노출 여부 (SURINP-608 b2cExposeYn — 모바일 카드 배지와 같은 이름, SURINP-908)
  b2cExposeYn: {
    Y: 'agGrid.b2cExposeOn',
    N: 'agGrid.b2cExposeOff'
  }
}

/**
 * 코드값을 화면 이름으로 바꾼다.
 *
 * @param {string} group - CODE_LABEL_KEYS 의 그룹 이름
 * @param {*} value - 코드값
 * @returns {string} 로케일 이름, 모르는 값이면 원래 값(null·undefined 는 '')
 */
export function codeLabel(group, value) {
  if (value === null || value === undefined) return ''
  // 표 자신의 항목만 본다 — 'constructor' 같은 상속 속성은 모르는 값이다
  const own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)
  const entries = own(CODE_LABEL_KEYS, group) ? CODE_LABEL_KEYS[group] : null
  const key = entries && own(entries, value) ? entries[value] : null
  return key ? i18n.global.t(key) : String(value)
}

/**
 * AG Grid colDef.valueFormatter 로 쓰는 변환기.
 *
 * @param {string} group - CODE_LABEL_KEYS 의 그룹 이름
 * @returns {function({value: *}): string}
 */
export function codeLabelFormatter(group) {
  return ({ value }) => codeLabel(group, value)
}
